/** @import { Signal } from "@preact/signals"; */
import { computed, signal, untracked } from "@preact/signals";

/** @typedef {"info" | "success" | "warning" | "error"} AlertType */

/**
 * @typedef {object} AlertAction
 * @property {string} text
 * @property {() => void} onSelect
 */

/**
 * @typedef {object} AlertItem
 * @property {symbol} id
 * @property {string} message
 * @property {AlertType} type
 * @property {number} ms
 * @property {AlertAction[]} actions
 */

/** @type {Signal<AlertItem[]>} */
const _alerts = signal([]);

export const alerts = computed(() => _alerts.value);

/**
 * @param {string} message
 * @param {object} [options]
 * @param {symbol} [options.id]
 * @param {AlertType} [options.type]
 * @param {number} [options.ms]
 * @param {AlertAction[]} [options.actions]
 */
export function alert(message, options = {}) {
	const { id = Symbol(), type = "info", ms = 5000, actions = [] } = options;

	// create an untracked copy of the alerts list
	// so this function doesn't subscribe the caller to alert changes
	const untrackedAlerts = untracked(() => _alerts.value);

	// try to find the index of the alert with the given id
	let i = -1;
	if (options.id) i = untrackedAlerts.findIndex(alert => alert.id === options.id);

	const next = { id, message, type, ms, actions };

	// if there's no alert with the given id, add a new one to the end
	if (i === -1) _alerts.value = untrackedAlerts.concat(next);
	// otherwise, replace the alert at the found index with the new one
	else _alerts.value = untrackedAlerts.slice(0, i).concat(next, untrackedAlerts.slice(i + 1));
}

/** @param {symbol} id */
export function dismiss(id) {
	_alerts.value = untracked(() => _alerts.value).filter(alert => alert.id !== id);
}
